'use client';

import { useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import {
  LiquidCardCarousel,
  type LiquidCard,
} from 'components/shared/LiquidCardCarousel/LiquidCardCarousel';
import { ArrowLink } from './ArrowLink';
import { SplitHeading } from './SplitHeading';
import { useTextReveal } from './hooks/useTextReveal';
import styles from './PowerPlugs.module.css';

const ASSET = 'web_v2/why-ultrahuman/why-ultrahuman-powerplugs';
const UI_ASSET = 'web_v2/why-ultrahuman/why-ultrahuman-powerplugs-ui';
/* Figma's card, which the photo boxes and overlay positions are measured on. */
const CARD = { w: 385, h: 513 } as const;

/** A box on the card in Figma px: x, y, width, height. */
type Box = [number, number, number, number];

type PowerPlug = {
  /** i18n key under `powerPlugs.cards.` in the whyUltrahuman namespace */
  key: string;
  /** File stem of `<file>-photo.png`, `<file>-ui.png` and `<file>-logo.png` */
  file: string;
  photo: Box;
  /** The card's UI art, exported at 4x from Figma 2095:11900 */
  art: Box;
  logo?: Box;
};

const POWER_PLUGS: PowerPlug[] = [
  {
    key: 'smartAlarm',
    file: 'smart-alarm',
    photo: [-148.03, 0, 681.05, 513],
    art: [47.89, 409.53, 291, 63],
  },
  {
    key: 'respiratoryHealth',
    file: 'respiratory-health',
    photo: [-191.95, 0, 681.05, 513],
    art: [58, 399.79, 272, 72],
  },
  {
    key: 'jetLag',
    file: 'social-jet-lag',
    photo: [-202.54, -9.14, 705.33, 531.29],
    art: [87, 391, 212, 83],
  },
  {
    key: 'circadianAlignment',
    file: 'circadian-alignment',
    photo: [-148.03, 0, 681.05, 513],
    art: [55, 409, 276, 64],
  },
  {
    key: 'screentime',
    file: 'screentime',
    photo: [-11.06, 0, 435.13, 513],
    art: [57.5, 408, 271, 65],
  },
  {
    key: 'caffeineWindow',
    file: 'caffeine-window',
    photo: [-211, -52, 751, 565],
    art: [48.5, 331.56, 288, 142],
  },
  {
    key: 'vitaminD',
    file: 'vitamin-d',
    photo: [-36.98, 0, 681.05, 513],
    art: [89.12, 365.51, 208, 148],
  },
  {
    key: 'shiftwork',
    file: 'shiftwork',
    photo: [-148.03, 0, 681.05, 513],
    art: [54, 392, 279, 82],
  },
  {
    key: 'pregnancyInsights',
    file: 'pregnancy-insights',
    photo: [0, 0, 681, 513],
    art: [62, 391, 261, 82],
  },
  {
    key: 'lesMills',
    file: 'les-mills',
    photo: [-338.47, -322.55, 880.95, 1179.84],
    art: [46, 391, 293, 82],
    logo: [117.65, 52.15, 141.5, 19],
  },
];

const boxStyle = ([x, y, w, h]: Box) => ({
  left: x,
  top: y,
  width: w,
  height: h,
});

/* A card's UI over its photo: Figma's bottom shade, which the UI art sits on,
   the art itself and, on Les Mills, the logo. */
const PlugArt = ({ plug }: { plug: PowerPlug }) => (
  <>
    <span className={styles.edge} />
    <span className={styles.shade} />
    {plug.logo ? (
      <CustomImage
        className={styles.art}
        style={boxStyle(plug.logo)}
        src={`${ASSET}/${plug.file}-logo.png`}
        alt=""
        width={Math.round(plug.logo[2] * 2)}
        height={Math.round(plug.logo[3] * 2)}
      />
    ) : null}
    <CustomImage
      className={styles.art}
      style={boxStyle(plug.art)}
      src={`${UI_ASSET}/${plug.file}-ui.png`}
      alt=""
      width={Math.round(plug.art[2] * 2)}
      height={Math.round(plug.art[3] * 2)}
    />
  </>
);

export const PowerPlugs = () => {
  const t = useTranslations('whyUltrahuman');
  const sectionRef = useRef<HTMLElement>(null);
  useTextReveal(sectionRef);

  const cards = useMemo<LiquidCard[]>(
    () =>
      POWER_PLUGS.map((plug) => {
        const [x, y, w, h] = plug.photo;
        return {
          key: plug.key,
          src: `${ASSET}/${plug.file}-photo.png`,
          alt: t(`powerPlugs.cards.${plug.key}.imageAlt`),
          crop: [x / CARD.w, y / CARD.h, w / CARD.w, h / CARD.h],
          overlay: <PlugArt plug={plug} />,
          caption: (
            <div className={styles.copy}>
              <p className={styles.cardLabel}>
                {t(`powerPlugs.cards.${plug.key}.label`)}
              </p>
              <h3 className={styles.cardTitle}>
                {t(`powerPlugs.cards.${plug.key}.title`)}
              </h3>
              <p className={styles.cardBody}>
                {t(`powerPlugs.cards.${plug.key}.body`)}
              </p>
            </div>
          ),
        };
      }),
    [t],
  );

  return (
    <section
      ref={sectionRef}
      id="powerplugs"
      className={styles.section}
      aria-labelledby="why-ultrahuman-powerplugs-title"
    >
      <SplitHeading
        titleId="why-ultrahuman-powerplugs-title"
        eyebrow={t('powerPlugs.eyebrow')}
        title={t('powerPlugs.title')}
        body={t('powerPlugs.body')}
        link={
          <ArrowLink href="/powerplugs" buttonType="why-uh powerplugs library">
            {t('powerPlugs.link')}
          </ArrowLink>
        }
      />

      <div className={styles.stage} data-reveal>
        <LiquidCardCarousel
          cards={cards}
          label={t('powerPlugs.aria.carousel')}
          sizes="(max-width: 992px) 160vw, 700px"
          designSize={[CARD.w, CARD.h]}
          link={false}
          className={styles.carousel}
        />
      </div>
    </section>
  );
};
